import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { getPaymentReminders } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { decimateAndFormatNumber } from '../../../utils/number';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';

const PAYMENT_REMINDER_HEADERS: ITableHeader[] = [
  {
    id: 'customer',
    name: 'Klant',
  },
  {
    id: 'invoiceNumbers',
    name: 'Factuur Nummer(s)',
  },
  {
    id: 'reminderType',
    name: 'Type',
  },
  {
    id: 'total',
    name: 'Totaal',
  },
  {
    id: 'createdAt',
    name: 'Datum',
    sortable: true,
  },
];

export const PaymentRemindersPage = () => {
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const [sortBy, setSortBy] = useState<'createdAt' | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getPaymentReminders(
        variables.cursor,
        variables.take,
        {
          search: searchValue,
        },
        sortBy,
        sortOrder,
      );
    },
    [searchValue, sortBy, sortOrder],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 100,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  useEffect(() => {
    page.reset();
  }, [searchValue, sortBy, sortOrder]);

  const title = 'Betaalherinneringen';
  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                Nieuwe herinnering
              </LinkButton>
            }
          />
        }
        panel
        filters={
          <Input
            type="text"
            placeholder="Zoek een betaalherinnering..."
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={PAYMENT_REMINDER_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as 'createdAt' | undefined);
            setSortOrder(order);
          }}
          mapData={(paymentReminder) => {
            return [
              <Link to={`${paymentReminder.id}`} className="link-text">
                {paymentReminder.customer.name}
              </Link>,
              paymentReminder.invoices.map((invoice) => invoice.invoice.invoiceNumber).join(', '),
              paymentReminder.reminderType,
              `€ ${decimateAndFormatNumber(
                paymentReminder.invoices.reduce((acc, i) => acc + i.amount, 0),
                2,
                {
                  decimalSeperator: ',',
                },
              )}`,
              formatDate(paymentReminder.createdAt),
            ];
          }}
        />
      </ListPage>
    </>
  );
};
